import { useState, useRef, useCallback, useEffect } from 'react';
import {
  IngestedFileRecord,
  PhotoRecord,
  PolarstepsTrip,
} from '../types/domain';
import {
  PipelineStatus,
  IngestionProgressStats,
  IngestionCompletionSummary,
  LivePreviewItem,
} from '../types/ingestion';
import {
  safeParseLocationsJson,
  safeParseTripJson,
  fusePolarstepsData,
} from '../services/polarstepsParser';
import { interpolateGpsLocation } from '../services/gpsInterpolator';
import { storageService as defaultStorageService, StorageService } from '../services/storage';
import { WorkerPool, ProcessPhotoResult } from '../workers/workerPool';
import { processPhoto } from '../workers/imageWorker';
import { PolarstepsLocationPoint, ParseResult } from '../types/polarsteps';

export interface UseIngestionPipelineOptions {
  workerPoolInstance?: WorkerPool;
  storageServiceInstance?: StorageService;
  throttleMs?: number;
}

const MAX_PREVIEW_ITEMS = 6;

export function useIngestionPipeline(options?: UseIngestionPipelineOptions) {
  const {
    workerPoolInstance,
    storageServiceInstance,
    throttleMs = 100,
  } = options || {};

  const activeWorkerPoolRef = useRef<WorkerPool | null>(workerPoolInstance || null);
  const activeStorageRef = useRef<StorageService>(storageServiceInstance || defaultStorageService);

  // Update refs if instances change
  useEffect(() => {
    if (workerPoolInstance) {
      activeWorkerPoolRef.current = workerPoolInstance;
    }
    if (storageServiceInstance) {
      activeStorageRef.current = storageServiceInstance;
    }
  }, [workerPoolInstance, storageServiceInstance]);

  const [status, setStatus] = useState<PipelineStatus>('idle');
  const [isPaused, setIsPaused] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Live Progress Stats exposed to React UI
  const [progressStats, setProgressStats] = useState<IngestionProgressStats>({
    totalCount: 0,
    processedCount: 0,
    failedCount: 0,
    progressPercent: 0,
    elapsedSeconds: 0,
    photosPerSecond: 0,
    estimatedRemainingSeconds: null,
    activeWorkers: 0,
    maxWorkers: 4,
    storage: {
      totalInputBytes: 0,
      totalThumbnailBytes: 0,
      totalPreviewBytes: 0,
      totalCompressedBytes: 0,
      spaceSavedBytes: 0,
      compressionRatioPercent: 0,
      averageThumbnailKb: 0,
      averagePreviewKb: 0,
      storageEngineType: 'OPFS',
    },
    matching: {
      exact: 0,
      interpolated: 0,
      snapFallback: 0,
      stepFallback: 0,
      unmatched: 0,
      totalGeotagged: 0,
      matchRatePercentage: 0,
    },
    recentThumbnails: [],
  });

  // Completion Summary
  const [completionSummary, setCompletionSummary] = useState<IngestionCompletionSummary | null>(null);

  // Internal mutable telemetry accumulator to prevent React render thrashing
  const accRef = useRef<{
    totalCount: number;
    processedCount: number;
    failedCount: number;
    startTime: number;
    totalInputBytes: number;
    totalThumbBytes: number;
    totalPreviewBytes: number;
    matching: {
      exact: number;
      interpolated: number;
      snapFallback: number;
      stepFallback: number;
      unmatched: number;
    };
    categoryMap: Map<string, { count: number; geotagged: number; bytes: number }>;
    recentPreviews: LivePreviewItem[];
    currentFileName: string;
    currentCategory: string;
    isPaused: boolean;
    isCancelled: boolean;
  }>({
    totalCount: 0,
    processedCount: 0,
    failedCount: 0,
    startTime: 0,
    totalInputBytes: 0,
    totalThumbBytes: 0,
    totalPreviewBytes: 0,
    matching: { exact: 0, interpolated: 0, snapFallback: 0, stepFallback: 0, unmatched: 0 },
    categoryMap: new Map(),
    recentPreviews: [],
    currentFileName: '',
    currentCategory: '',
    isPaused: false,
    isCancelled: false,
  });

  const timerRef = useRef<number | null>(null);

  // Clean up object URLs on unmount to prevent leaks
  useEffect(() => {
    return () => {
      accRef.current.recentPreviews.forEach((p) => {
        if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(p.objectUrl);
        }
      });
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Flush accumulator to React state
  const flushProgressToState = useCallback(() => {
    const acc = accRef.current;
    if (acc.totalCount === 0) return;

    const elapsed = Math.max(0.1, (performance.now() - acc.startTime) / 1000);
    const speed = acc.processedCount / elapsed;
    const remaining = acc.totalCount - acc.processedCount;
    const eta = speed > 0 ? Math.ceil(remaining / speed) : null;
    const totalComp = acc.totalThumbBytes + acc.totalPreviewBytes;
    const saved = Math.max(0, acc.totalInputBytes - totalComp);
    const ratio = acc.totalInputBytes > 0 ? Math.round((saved / acc.totalInputBytes) * 100) : 0;
    const avgThumb = acc.processedCount > 0 ? acc.totalThumbBytes / acc.processedCount / 1024 : 0;
    const avgPrev = acc.processedCount > 0 ? acc.totalPreviewBytes / acc.processedCount / 1024 : 0;
    const totalGeo =
      acc.matching.exact + acc.matching.interpolated + acc.matching.snapFallback + acc.matching.stepFallback;
    const matchRate = acc.processedCount > 0 ? (totalGeo / acc.processedCount) * 100 : 0;

    const pool = activeWorkerPoolRef.current;
    const activeWorkers = acc.isPaused ? 0 : pool ? pool.activeWorkers : 0;
    const maxWorkers = pool ? pool.concurrency : 4;

    setProgressStats({
      totalCount: acc.totalCount,
      processedCount: acc.processedCount,
      failedCount: acc.failedCount,
      progressPercent: Math.min(100, (acc.processedCount / acc.totalCount) * 100),
      elapsedSeconds: Math.floor(elapsed),
      photosPerSecond: Math.round(speed * 10) / 10,
      estimatedRemainingSeconds: eta,
      activeWorkers,
      maxWorkers,
      currentFileName: acc.currentFileName,
      currentCategory: acc.currentCategory,
      storage: {
        totalInputBytes: acc.totalInputBytes,
        totalThumbnailBytes: acc.totalThumbBytes,
        totalPreviewBytes: acc.totalPreviewBytes,
        totalCompressedBytes: totalComp,
        spaceSavedBytes: saved,
        compressionRatioPercent: ratio,
        averageThumbnailKb: avgThumb,
        averagePreviewKb: avgPrev,
        storageEngineType: 'OPFS',
      },
      matching: {
        ...acc.matching,
        totalGeotagged: totalGeo,
        matchRatePercentage: matchRate,
      },
      recentThumbnails: [...acc.recentPreviews],
    });
  }, []);

  // Launch pipeline
  const startIngestion = useCallback(
    async (
      photos: IngestedFileRecord[],
      polarstepsFiles: { locationsFile?: File; tripFile?: File },
      options?: {
        targetTripId?: string | number;
        targetTripName?: string;
        mode?: 'append' | 'replace';
      }
    ) => {
      if (photos.length === 0) return;

      const targetTripId = options?.targetTripId || 'islanda-trip';
      const targetTripName = options?.targetTripName || 'La nostra Islanda';
      const mode = options?.mode || 'append';

      setStatus('parsing_polarsteps');
      setErrorMessage(null);

      // 1. Initialize WorkerPool if not already instantiated
      if (!activeWorkerPoolRef.current) {
        activeWorkerPoolRef.current = new WorkerPool();
      }
      const pool = activeWorkerPoolRef.current;
      const storage = activeStorageRef.current;

      // 2. Parse Polarsteps Files & Resolve Trip
      let trip: PolarstepsTrip;
      try {
        let locationsResult: ParseResult<PolarstepsLocationPoint[]> | undefined;
        let tripResult: ParseResult<PolarstepsTrip> | undefined;

        if (polarstepsFiles.locationsFile) {
          const locText = await polarstepsFiles.locationsFile.text();
          locationsResult = safeParseLocationsJson(locText);
          const tripCandidate = safeParseTripJson(locText);
          if (tripCandidate.success && tripCandidate.data) {
            tripResult = tripCandidate;
          }
        }

        if (polarstepsFiles.tripFile && polarstepsFiles.tripFile !== polarstepsFiles.locationsFile) {
          const tripText = await polarstepsFiles.tripFile.text();
          const parsedTrip = safeParseTripJson(tripText);
          if (parsedTrip.success && parsedTrip.data) {
            tripResult = parsedTrip;
          }
          if (!locationsResult || !locationsResult.success || (locationsResult.data?.length ?? 0) === 0) {
            const locCandidate = safeParseLocationsJson(tripText);
            if (locCandidate.success && (locCandidate.data?.length ?? 0) > 0) {
              locationsResult = locCandidate;
            }
          }
        }

        const fusion = fusePolarstepsData(locationsResult, tripResult);

        // Retrieve existing trip if any
        const storedTrips = (await storage?.getAllTrips?.()) || [];
        const existingTrip = storedTrips.find(
          (t) => t.id === targetTripId || t.name === targetTripName
        );

        if (fusion.data && fusion.data.locations && fusion.data.locations.length > 0) {
          trip = {
            id: targetTripId,
            name: targetTripName,
            start_date: fusion.data.start_date || existingTrip?.start_date || Math.floor(Date.now() / 1000),
            end_date: fusion.data.end_date || existingTrip?.end_date || Math.floor(Date.now() / 1000),
            timezone_id: fusion.data.timezone_id || existingTrip?.timezone_id || 'Atlantic/Reykjavik',
            all_steps: fusion.data.all_steps || existingTrip?.all_steps || [],
            locations: fusion.data.locations,
          };
        } else if (existingTrip) {
          trip = existingTrip;
        } else {
          trip = {
            id: targetTripId,
            name: targetTripName,
            start_date: Math.floor(Date.now() / 1000),
            end_date: Math.floor(Date.now() / 1000),
            timezone_id: 'Atlantic/Reykjavik',
            all_steps: [],
            locations: [],
          };
        }

        // Handle replacement mode if specified
        if (mode === 'replace') {
          const storedPhotos = (await storage?.getPhotos?.()) || [];
          const tripPhotos = storedPhotos.filter((p) => p.tripId === targetTripId);
          for (const p of tripPhotos) {
            await storage?.deletePhoto?.(p.id);
          }
        }

        // Persist trip metadata
        if (storage?.saveTrip) {
          await storage.saveTrip(trip);
        }
      } catch (err: unknown) {
        setErrorMessage(
          `Errore lettura file Polarsteps: ${err instanceof Error ? err.message : String(err)}`
        );
        setStatus('error');
        return;
      }

      // 3. Initialize Telemetry Accumulator
      const totalInputBytes = photos.reduce((acc, p) => acc + (p.file?.size || 0), 0);
      accRef.current = {
        totalCount: photos.length,
        processedCount: 0,
        failedCount: 0,
        startTime: performance.now(),
        totalInputBytes,
        totalThumbBytes: 0,
        totalPreviewBytes: 0,
        matching: { exact: 0, interpolated: 0, snapFallback: 0, stepFallback: 0, unmatched: 0 },
        categoryMap: new Map(),
        recentPreviews: [],
        currentFileName: '',
        currentCategory: '',
        isPaused: false,
        isCancelled: false,
      };

      // 4. Start Throttling Interval
      setStatus('processing');
      setIsPaused(false);
      timerRef.current = window.setInterval(flushProgressToState, throttleMs);

      // 5. Parallel Batch Dispatch through Worker Pool
      const errors: { fileName: string; reason: string }[] = [];
      let nextIndex = 0;
      const concurrency = pool.concurrency || 4;

      const runNextWorker = async (): Promise<void> => {
        while (nextIndex < photos.length && !accRef.current.isCancelled) {
          while (accRef.current.isPaused && !accRef.current.isCancelled) {
            await new Promise((r) => setTimeout(r, 100));
          }
          if (accRef.current.isCancelled) break;

          const currentIndex = nextIndex++;
          const photo = photos[currentIndex];
          accRef.current.currentFileName = photo.file.name;
          accRef.current.currentCategory = photo.category;

          try {
            const job = {
              id: `p_${currentIndex}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
              file: photo.file,
              category: photo.category,
              tripTimezone: trip.timezone_id || 'UTC',
            };

            let result: ProcessPhotoResult;
            try {
              result = await pool.enqueue(job);
            } catch (workerErr) {
              console.warn(
                `[Pipeline] Worker pool non disponibile o fallito per ${photo.file.name}, elaborazione nel thread principale:`,
                workerErr
              );
              result = await processPhoto(job);
            }

            // Instant GPS interpolation with 100% geotagging guarantee fallback
            const match = interpolateGpsLocation(
              result.timestampUtc,
              trip.locations,
              trip.all_steps,
              { fallbackToNearest: true }
            );

            // Construct photo record
            const record: PhotoRecord = {
              id: result.id,
              tripId: targetTripId,
              fileName: photo.file.name,
              category: photo.category,
              dateTimeOriginal: result.dateTimeOriginal,
              timestampUtc: result.timestampUtc,
              lat: match.lat,
              lon: match.lon,
              matchingConfidence: match.matchingConfidence,
              stepName: match.stepName,
              thumbnailKey: `thumb_${result.id}.webp`,
              previewKey: `prev_${result.id}.webp`,
              thumbnailSize: result.thumbnailBlob.size,
              previewSize: result.previewBlob.size,
              width: result.width,
              height: result.height,
              exif: result.exif,
            };

            // Stream directly to hybrid storage (Dexie + OPFS)
            if (storage?.savePhoto) {
              await storage.savePhoto(record, result.thumbnailBlob, result.previewBlob);
            }

            // Update telemetry
            accRef.current.processedCount++;
            accRef.current.totalThumbBytes += result.thumbnailBlob.size;
            accRef.current.totalPreviewBytes += result.previewBlob.size;

            if (match.matchingConfidence === 'exact') accRef.current.matching.exact++;
            else if (match.matchingConfidence === 'interpolated') accRef.current.matching.interpolated++;
            else if (match.matchingConfidence === 'snap_fallback') accRef.current.matching.snapFallback++;
            else if (match.matchingConfidence === 'step_fallback') accRef.current.matching.stepFallback++;
            else accRef.current.matching.unmatched++;

            // Category breakdown tracking
            const catItem = accRef.current.categoryMap.get(photo.category) || {
              count: 0,
              geotagged: 0,
              bytes: 0,
            };
            catItem.count++;
            if (match.lat !== null && match.lon !== null) catItem.geotagged++;
            catItem.bytes += result.thumbnailBlob.size + result.previewBlob.size;
            accRef.current.categoryMap.set(photo.category, catItem);

            // Sliding window of live preview thumbnails (max 6)
            let thumbUrl = '';
            if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
              thumbUrl = URL.createObjectURL(result.thumbnailBlob);
            } else {
              thumbUrl = `blob:preview-${result.id}`;
            }

            if (accRef.current.recentPreviews.length >= MAX_PREVIEW_ITEMS) {
              const old = accRef.current.recentPreviews.shift();
              if (
                old &&
                typeof URL !== 'undefined' &&
                typeof URL.revokeObjectURL === 'function' &&
                old.objectUrl.startsWith('blob:')
              ) {
                URL.revokeObjectURL(old.objectUrl);
              }
            }

            accRef.current.recentPreviews.push({
              id: result.id,
              objectUrl: thumbUrl,
              fileName: photo.file.name,
              category: photo.category,
              timestamp: result.dateTimeOriginal,
              confidence: match.matchingConfidence,
            });
          } catch (err: unknown) {
            console.error(`[Pipeline] Errore elaborazione foto ${photo.file.name}:`, err);
            accRef.current.failedCount++;
            errors.push({
              fileName: photo.file.name,
              reason: err instanceof Error ? err.message : String(err),
            });
          }
        }
      };

      try {
        const workerCount = Math.min(concurrency, photos.length);
        const workers = Array.from({ length: workerCount }, () => runNextWorker());
        await Promise.all(workers);
      } finally {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        flushProgressToState();
      }

      // 6. Build Completion Summary
      const finalDuration = Math.max(0.1, (performance.now() - accRef.current.startTime) / 1000);
      const categoriesSummary = Array.from(accRef.current.categoryMap.entries()).map(
        ([category, data]) => ({
          category,
          photoCount: data.count,
          geotaggedCount: data.geotagged,
          geotaggedPercent: data.count > 0 ? (data.geotagged / data.count) * 100 : 0,
          totalStorageBytes: data.bytes,
        })
      );

      const totalGeo =
        accRef.current.matching.exact +
        accRef.current.matching.interpolated +
        accRef.current.matching.snapFallback +
        accRef.current.matching.stepFallback;

      const totalCompBytes = accRef.current.totalThumbBytes + accRef.current.totalPreviewBytes;
      const savedBytes = Math.max(0, accRef.current.totalInputBytes - totalCompBytes);
      const compRatio =
        accRef.current.totalInputBytes > 0
          ? Math.round((savedBytes / accRef.current.totalInputBytes) * 100)
          : 0;

      setCompletionSummary({
        trip,
        totalPhotos: photos.length,
        processedPhotos: accRef.current.processedCount,
        failedPhotos: accRef.current.failedCount,
        durationSeconds: Math.floor(finalDuration),
        averageThroughput: accRef.current.processedCount / finalDuration,
        storage: {
          totalInputBytes: accRef.current.totalInputBytes,
          totalThumbnailBytes: accRef.current.totalThumbBytes,
          totalPreviewBytes: accRef.current.totalPreviewBytes,
          totalCompressedBytes: totalCompBytes,
          spaceSavedBytes: savedBytes,
          compressionRatioPercent: compRatio,
          averageThumbnailKb:
            accRef.current.processedCount > 0
              ? accRef.current.totalThumbBytes / accRef.current.processedCount / 1024
              : 0,
          averagePreviewKb:
            accRef.current.processedCount > 0
              ? accRef.current.totalPreviewBytes / accRef.current.processedCount / 1024
              : 0,
          storageEngineType: 'OPFS',
        },
        matching: {
          ...accRef.current.matching,
          totalGeotagged: totalGeo,
          matchRatePercentage:
            accRef.current.processedCount > 0
              ? (totalGeo / accRef.current.processedCount) * 100
              : 0,
        },
        categories: categoriesSummary,
        reconciledTimezone: trip.timezone_id || 'UTC',
        timezoneTier: 'Tier 3 (Polarsteps Trip Timezone)',
        errors,
      });

      // If the ingestion was cancelled mid-flight, do not trigger completed celebration
      if (accRef.current.isCancelled) {
        setStatus('idle');
        return;
      }
      setStatus('completed');
    },
    [throttleMs, flushProgressToState]
  );

  const pause = useCallback(() => {
    accRef.current.isPaused = true;
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    accRef.current.isPaused = false;
    setIsPaused(false);
  }, []);

  const cancel = useCallback(() => {
    accRef.current.isCancelled = true;
    accRef.current.isPaused = false;
    setIsPaused(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (activeWorkerPoolRef.current?.terminate) {
      activeWorkerPoolRef.current.terminate();
    }
    activeWorkerPoolRef.current = null;
    setStatus('idle');
  }, []);

  const reset = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    accRef.current.recentPreviews.forEach((p) => {
      if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(p.objectUrl);
      }
    });
    accRef.current.recentPreviews = [];
    if (activeWorkerPoolRef.current?.terminate) {
      activeWorkerPoolRef.current.terminate();
    }
    activeWorkerPoolRef.current = null;
    setStatus('idle');
    setCompletionSummary(null);
    setErrorMessage(null);
  }, []);

  return {
    status,
    isPaused,
    errorMessage,
    progressStats,
    completionSummary,
    startIngestion,
    pause,
    resume,
    cancel,
    reset,
  };
}
